import { useEffect } from "react";

/**
 * usePageMeta — métadonnées propres à une page, dans une SPA sans SSR.
 *
 * `public/index.html` est servi à l'identique sur toutes les routes : sans ce
 * hook, chaque page hérite du titre, de la description et du canonical de
 * l'accueil. Google indexerait donc dix pages présentées comme la même.
 *
 * Le crawler de Google exécute le JavaScript : les balises posées ici sont bien
 * lues. Ce n'est pas l'équivalent d'un rendu serveur — l'indexation passe par la
 * file d'attente de rendu, donc plus lentement — mais c'est ce qui distingue une
 * page indexable d'une page invisible, sans ajouter de dépendance.
 *
 * Tout est retiré au démontage : une navigation côté client ne doit jamais
 * laisser le titre ou le JSON-LD d'une page sur la suivante.
 *
 * @param {object}  meta
 * @param {string}  meta.title        titre complet de l'onglet
 * @param {string}  meta.description  meta description
 * @param {string}  meta.canonical    URL canonique absolue
 * @param {object=} meta.jsonLd       données structurées propres à la page
 * @param {string=} meta.ogImage      image de partage absolue
 */
export default function usePageMeta({ title, description, canonical, jsonLd, ogImage }) {
  useEffect(() => {
    const previousTitle = document.title;
    const created = [];

    if (title) document.title = title;

    /* Les balises déjà présentes dans index.html sont réutilisées et restaurées
       telles quelles ; celles qui manquent sont créées puis supprimées. */
    const touched = [];
    const setTag = (selector, create, attr, value) => {
      if (!value) return;
      let el = document.head.querySelector(selector);
      if (el) {
        touched.push([el, attr, el.getAttribute(attr)]);
      } else {
        el = create();
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute(attr, value);
    };

    setTag('meta[name="description"]',
      () => Object.assign(document.createElement("meta"), { name: "description" }),
      "content", description);

    setTag('meta[property="og:title"]',
      () => { const m = document.createElement("meta"); m.setAttribute("property", "og:title"); return m; },
      "content", title);

    setTag('meta[property="og:description"]',
      () => { const m = document.createElement("meta"); m.setAttribute("property", "og:description"); return m; },
      "content", description);

    setTag('meta[property="og:url"]',
      () => { const m = document.createElement("meta"); m.setAttribute("property", "og:url"); return m; },
      "content", canonical);

    setTag('meta[property="og:image"]',
      () => { const m = document.createElement("meta"); m.setAttribute("property", "og:image"); return m; },
      "content", ogImage);

    setTag('link[rel="canonical"]',
      () => Object.assign(document.createElement("link"), { rel: "canonical" }),
      "href", canonical);

    let script = null;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(jsonLd);
      script.dataset.pageMeta = "true";
      document.head.appendChild(script);
    }

    return () => {
      document.title = previousTitle;
      touched.forEach(([el, attr, value]) => {
        if (value === null) el.removeAttribute(attr);
        else el.setAttribute(attr, value);
      });
      created.forEach((el) => el.remove());
      if (script) script.remove();
    };
  }, [title, description, canonical, jsonLd, ogImage]);
}
